import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import StandardFilterPanel from '../../components/hr/StandardFilterPanel';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  message,
  Modal,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  NotificationOutlined,
  DownOutlined,
  DeleteOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { getLast30DaysRange } from '../../utils/dateUtils';
import { noticesApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { PANEL_PRIMARY_BTN_CLASS, BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../constants/ui';
import { useListReturnState } from '../../hooks/useListReturnState';

const Notices = () => {
  const navigate = useNavigate();
  const { canDelete, canEditRecord, canDeleteRecord } = useResourcePermissions('notices');

  const defaultRange = useMemo(() => getLast30DaysRange('jYYYY-jMM-jDD'), []);
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    search: '',
  });
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      pagination: {
        current_page: pagination.current_page,
        per_page: pagination.per_page,
      },
    }),
    [filters, pagination.current_page, pagination.per_page],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current_page: restored.pagination.current_page || 1,
        per_page: restored.pagination.per_page || prev.per_page,
      }));
    }
  });

  const fetchRows = async (page = pagination.current_page, perPage = pagination.per_page) => {
    setLoading(true);
    try {
      const res = await noticesApi.getAll({
        startDate: filters.startDate,
        endDate: filters.endDate,
        search: filters.search || '',
        page,
        per_page: perPage,
      });
      setRows(res?.notices || []);
      setPagination(
        res?.pagination || { current_page: page, per_page: perPage, total: 0, last_page: 1 }
      );
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت اعلانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(pagination.current_page, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current_page: 1 }));
      fetchRows(1, pagination.per_page);
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value ?? '' }));
  };

  const handleResetFilters = () => {
    const dr = getLast30DaysRange('jYYYY-jMM-jDD');
    setFilters({
      startDate: dr.startDate,
      endDate: dr.endDate,
      search: '',
    });
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'حذف اعلان',
      content: 'آیا از حذف این اعلان اطمینان دارید؟',
      okText: 'حذف',
      cancelText: 'لغو',
      okButtonProps: {
        danger: true,
        style: { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' },
      },
      onOk: async () => {
        try {
          await noticesApi.delete(id);
          message.success('اعلان حذف شد');
          fetchRows(pagination.current_page, pagination.per_page);
        } catch (e) {
          message.error(e?.message || 'خطا در حذف اعلان');
        }
      },
    });
  };

  const handleBulkDelete = () => {
    Modal.confirm({
      title: 'حذف گروهی اعلانات',
      content: `حذف ${selectedRowKeys.length} مورد انجام شود؟`,
      okText: 'حذف',
      cancelText: 'لغو',
      okButtonProps: {
        danger: true,
        style: { backgroundColor: '#dc2626', borderColor: '#dc2626', color: '#fff' },
      },
      onOk: async () => {
        setBulkLoading(true);
        try {
          await noticesApi.applyQuickAction('delete', selectedRowKeys);
          message.success('حذف گروهی انجام شد');
          setSelectedRowKeys([]);
          fetchRows(pagination.current_page, pagination.per_page);
        } catch (e) {
          message.error(e?.message || 'خطا در حذف گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      await noticesApi.exportFile();
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت خروجی');
    } finally {
      setExportLoading(false);
    }
  };

  const quickFilters = [];

  const advancedFilters = [
    {
      key: 'startDate',
      type: 'date',
      label: 'از تاریخ',
      placeholder: 'از تاریخ',
      value: filters.startDate,
    },
    {
      key: 'endDate',
      type: 'date',
      label: 'تا تاریخ',
      placeholder: 'تا تاریخ',
      value: filters.endDate,
    },
  ];

  const columns = [
    {
      title: 'عنوان',
      dataIndex: 'heading',
      key: 'heading',
      render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>,
    },
    {
      title: 'ارسال برای',
      dataIndex: 'to',
      key: 'to',
      width: 130,
      render: (to) => (
        <Tag color={to === 'client' ? 'purple' : 'blue'} style={{ fontFamily: 'CustomFont' }}>
          {to === 'client' ? 'مشتری' : 'کارمند'}
        </Tag>
      ),
    },
    {
      title: 'تاریخ',
      dataIndex: 'notice_date',
      key: 'notice_date',
      width: 180,
      render: (d) => <span style={{ fontFamily: 'CustomFont' }}>{d || '—'}</span>,
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 160,
      render: (_, row) => (
        <Space size="small">
          <Button type="text" icon={<EyeOutlined />} onClick={() => navigateToDetail(`/notices/${row.id}`)} />
          {canEditRecord(row) ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigateToDetail(`/notices/${row.id}/edit`)}
            />
          ) : null}
          {canDeleteRecord(row) ? (
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row.id)} />
          ) : null}
        </Space>
      ),
    },
  ];

  const bulkItems = [
    {
      key: 'delete',
      label: 'حذف',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
  ];

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                <NotificationOutlined className="text-indigo-600 dark:text-indigo-400 text-xl" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">اعلانات</h1>
            </div>
            <Space wrap>
              <Button icon={<ExportOutlined />} onClick={handleExport} loading={exportLoading}>
                خروجی
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchRows(pagination.current_page, pagination.per_page)}
                loading={loading}
              >
                بروزرسانی
              </Button>
              <PermissionGate permission="add_notice">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  className={PANEL_PRIMARY_BTN_CLASS}
                  onClick={() => navigate('/notices/add')}
                >
                  اعلان جدید
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {selectedRowKeys.length > 0 && canDelete ? (
              <div className={BULK_ACTION_BAR_CLASS}>
                <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
                  {selectedRowKeys.length} مورد انتخاب شده
                </span>
                <Dropdown menu={{ items: bulkItems }} trigger={['click']} disabled={bulkLoading}>
                  <Button
                    type="primary"
                    loading={bulkLoading}
                    size="middle"
                    className="border-0"
                    icon={<DownOutlined />}
                    style={{ fontFamily: 'CustomFont', backgroundColor: '#f97316', color: '#fff' }}
                  >
                    عملیات گروهی
                  </Button>
                </Dropdown>
              </div>
            ) : null}

            <div className="mb-6">
              <StandardFilterPanel
                searchValue={filters.search}
                onSearchChange={(v) => handleFilterChange('search', v || '')}
                searchPlaceholder="جستجو عنوان اعلان..."
                quickFilters={quickFilters}
                advancedFilters={advancedFilters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
                totalCount={pagination.total}
                showAdvancedFilters
                showTotalCount
              />
            </div>

            {loading ? (
              <TableSkeleton rows={8} columns={4} />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                rowSelection={
                  canDelete
                    ? {
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                      }
                    : undefined
                }
                pagination={{
                  current: pagination.current_page,
                  pageSize: pagination.per_page,
                  total: pagination.total,
                  showSizeChanger: true,
                  onChange: (page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      current_page: page,
                      per_page: pageSize,
                    }));
                  },
                }}
              />
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Notices;
